import { useCallback, useState } from 'react';

import { useResizeObserver } from '@tanium/react-use-resize-observer';

interface UseContainerDimensionsProps {
  width?: number | string;
  height?: number | string;
}

interface ContainerDimensions<TElement> {
  /**
   * A ref to attach to the container Element.
   */
  containerRef: (node: TElement) => void;
  /**
   * The computed dimensions of container Element in pixels.
   */
  dimensions: {
    width: number;
    height: number;
  };
  /**
   * The container has obtained actual height from resize observer
   */
  hasResizeObserverHeight: boolean;
}

export const useContainerDimensions = <TElement extends Element = HTMLDivElement>({
  height: heightProp,
  width: widthProp,
}: UseContainerDimensionsProps): ContainerDimensions<TElement> => {
  const [dimensions, setDimensions] = useState<{ width: number; height: number }>({
    width: typeof widthProp === 'number' ? widthProp : 0,
    height: typeof heightProp === 'number' ? heightProp : 0,
  });

  const [hasResizeObserverHeight, setHasResizeObserverHeight] = useState(false);

  const ref = useResizeObserver(
    useCallback(({ contentRect: { height, width } }) => {
      setDimensions({ height, width });
      setHasResizeObserverHeight(true);
    }, []),
  );

  return {
    containerRef: ref,
    dimensions,
    hasResizeObserverHeight,
  };
};
